import { blur, focus } from './focusApi'

/** @type {{ current: any }} */
const activeNewAnswerRef = { current: null }
/** @type {{ current: any }} */
const sideNewAnswerRef = { current: null }
/** @type {{ current: any }} */
const activeQuestEditorRef = { current: null }
/** @type {{ current: any }} */
const sideQuestEditorRef = { current: null }
/** @type {{ current: any }} */
const newQuestEditorRef = { current: null }
/** @type {{ current: any }} */
let middleSearchBarRef = { current: null }
/** @type {{ current: any }} */
let rightSearchBarRef = { current: null }

const editorRefs = {
    activeNewAnswerRef,
    sideNewAnswerRef,
    activeQuestEditorRef,
    sideQuestEditorRef,
    newQuestEditorRef,
    middleSearchBarRef,
    rightSearchBarRef,
}

// Track Android IME composition state globally to coordinate scroll/edits
let imeState = { composing: false }

export function setIMEComposing(val) {
    imeState.composing = !!val
}

export function isIMEComposing() {
    return !!imeState.composing
}

export function getEditorRef(ref) {
    if (ref) return editorRefs[ref]
    else return null
}

export function assignMiddleSearchBarRef(ref) {
    editorRefs.middleSearchBarRef = ref
}

export function focusMiddleSearchBar() {
    focus(editorRefs.middleSearchBarRef)
}

export function assignRightSearchBarRef(ref) {
    editorRefs.rightSearchBarRef = ref
}

export function focusRightSearchBar() {
    focus(editorRefs.rightSearchBarRef)
}

export function assignActiveNewAnswerRef(ref) {
    editorRefs.activeNewAnswerRef.current = ref
}

export function focusActiveNewAnswer() {
    focus(editorRefs.activeNewAnswerRef?.current?.richEditor?.current)
}

export function assignSideNewAnswerRef(ref) {
    editorRefs.sideNewAnswerRef.current = ref
}

export function focusSideNewAnswer() {
    focus(editorRefs.sideNewAnswerRef?.current?.richEditor?.current)
}

function blurEditorObject(editor) {
    let didBlur = false
    if (!editor) return didBlur

    try {
        if (typeof editor.blur === 'function') {
            editor.blur()
            didBlur = true
        }
    } catch (e) {}

    try {
        if (editor?.commands?.blur) {
            editor.commands.blur()
            didBlur = true
        }
    } catch (e) {}

    let actualEditor = null
    try {
        actualEditor = editor?.getEditor ? editor.getEditor() : null
    } catch (e) {}

    if (actualEditor && actualEditor !== editor) {
        didBlur = blurEditorObject(actualEditor) || didBlur
    }

    try {
        const dom =
            actualEditor?.view?.dom || editor?.view?.dom || actualEditor?.root || editor?.root
        if (dom && typeof dom.blur === 'function') {
            dom.blur()
            didBlur = true
        }
    } catch (e) {}

    return didBlur
}

function blurNewAnswerRef(ref, options = {}) {
    const component = ref?.current
    if (!component) return false

    const container = component.containerRef?.current
    const activeEl = document.activeElement
    const activeInside = !!(container && activeEl instanceof Node && container.contains(activeEl))

    const force = options.force === true
    let editorFocused = false
    try {
        const editor = component.richEditor?.current
        editorFocused =
            editor?.hasFocus?.() === true ||
            editor?.getEditor?.()?.hasFocus?.() === true ||
            editor?.getEditor?.()?.isFocused === true
    } catch (e) {}

    if (!force && !activeInside && !editorFocused) return false

    let didBlur = false
    try {
        if (typeof component.blurEditor === 'function') {
            component.blurEditor()
            didBlur = true
        }

        const editor = component.richEditor?.current
        didBlur = blurEditorObject(editor) || didBlur

        if (activeEl instanceof HTMLElement && typeof activeEl.blur === 'function') {
            activeEl.blur()
            didBlur = true
        }
    } catch (e) {}

    // A phone's folded composer pill opens on focus and folds when focus and
    // the pointer both leave it. The keyboard being dismissed is that leave,
    // whether or not the focus above had anything to blur.
    if (options.foldCompact === true) {
        try {
            component.foldCompactComposerForKeyboardDismiss?.()
        } catch (e) {}
    }

    return didBlur
}

export function blurFocusedNewAnswer(options = {}) {
    const didBlurActive = blurNewAnswerRef(editorRefs.activeNewAnswerRef, options)
    const didBlurSide = blurNewAnswerRef(editorRefs.sideNewAnswerRef, options)
    return didBlurActive || didBlurSide
}

export function assignActiveQuestEditorRef(ref) {
    editorRefs.activeQuestEditorRef.current = ref
}

export function assignSideQuestEditorRef(ref) {
    editorRefs.sideQuestEditorRef.current = ref
}

export function assignNewQuestEditorRef(ref) {
    editorRefs.newQuestEditorRef.current = ref
}

export function focusActiveQuestEditor() {
    focus(editorRefs.activeQuestEditorRef)
}

export function focusSideQuestEditor() {
    focus(editorRefs.sideQuestEditorRef)
}

export function focusNewQuestEditor() {
    focus(editorRefs.newQuestEditorRef)
}

export function blurNewQuestEditor() {
    blur(editorRefs.newQuestEditorRef)
}
